import { createContext, useCallback, useContext, useRef, useState } from "react";
import toast from "react-hot-toast";
import ClientInactivityConfirmationModal from "../components/client/ClientInactivityConfirmationModal";
import {
    getInactiveConfirmationKey,
    requiresInactiveReasonConfirmation,
} from "../helpers/clientActivityTransition";
import { confirmClientInactivity } from "../services/client/confirmClientInactivity";
import { getClient } from "../services/getClient";

const ClientInactivityConfirmationContext = createContext({
    requestInactiveConfirmation: async () => ({ status: "not-required" }),
});

const getValue = (object, camelKey, pascalKey) =>
    object?.[camelKey] ?? object?.[pascalKey];

const isVerifiedStaleResolution = (client, transition) => {
    if (!client) return false;

    const isActive = getValue(client, "isActive", "IsActive");
    const inactiveReason = getValue(client, "inactiveReason", "InactiveReason");
    const inactiveSince = getValue(client, "inactiveSince", "InactiveSince");

    return (
        isActive === true ||
        (typeof inactiveReason === "string" && inactiveReason.trim().length > 0) ||
        (inactiveSince && inactiveSince !== transition.inactiveSince)
    );
};

export const ClientInactivityConfirmationProvider = ({ children }) => {
    const [activeRequest, setActiveRequest] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");
    const activeRequestRef = useRef(null);
    const queueRef = useRef([]);
    const pendingByKeyRef = useRef(new Map());
    const handledResultsRef = useRef(new Map());

    const showNext = useCallback(() => {
        const next = queueRef.current.shift() || null;
        activeRequestRef.current = next;
        setErrorMessage("");
        setActiveRequest(next);
    }, []);

    const finishActiveRequest = useCallback(
        (result) => {
            const request = activeRequestRef.current;
            if (!request) return;

            pendingByKeyRef.current.delete(request.key);
            if (
                result?.status === "confirmed" ||
                result?.status === "stale-resolved"
            ) {
                handledResultsRef.current.set(request.key, result);
            }
            request.resolve(result);
            activeRequestRef.current = null;
            setActiveRequest(null);
            setErrorMessage("");

            Promise.resolve().then(showNext);
        },
        [showNext],
    );

    const requestInactiveConfirmation = useCallback((transition) => {
        if (!requiresInactiveReasonConfirmation(transition)) {
            return Promise.resolve({ status: "not-required" });
        }

        const key = getInactiveConfirmationKey(transition);
        if (handledResultsRef.current.has(key)) {
            return Promise.resolve(handledResultsRef.current.get(key));
        }
        const existing = pendingByKeyRef.current.get(key);
        if (existing) return existing;

        let resolveRequest;
        const promise = new Promise((resolve) => {
            resolveRequest = resolve;
        });
        const request = { key, transition, resolve: resolveRequest };
        pendingByKeyRef.current.set(key, promise);

        if (activeRequestRef.current) {
            queueRef.current.push(request);
        } else {
            activeRequestRef.current = request;
            setErrorMessage("");
            setActiveRequest(request);
        }

        return promise;
    }, []);

    const handleConfirm = async (payload) => {
        const request = activeRequestRef.current;
        if (!request) return;

        setErrorMessage("");
        const response = await confirmClientInactivity(
            request.transition.clientId,
            payload,
        );

        if (response.success && response?.data?.success !== false) {
            finishActiveRequest({ status: "confirmed", data: response.data });
            return;
        }

        if (response.status === 409) {
            const clientResponse = await getClient(request.transition.clientId);
            if (
                clientResponse?.success &&
                isVerifiedStaleResolution(clientResponse.data, request.transition)
            ) {
                toast(
                    "This inactivity confirmation is no longer applicable. Client details were refreshed.",
                    { icon: "ℹ️" },
                );
                finishActiveRequest({
                    status: "stale-resolved",
                    data: clientResponse.data,
                });
                return;
            }

            setErrorMessage(
                response.message ||
                    "This confirmation conflicts with newer client activity. Refresh the client and try again.",
            );
            return;
        }

        if (response.status === 400) {
            setErrorMessage(
                response.message ||
                    "Review the selected answer and explanation, then try again.",
            );
            return;
        }

        if (response.status === 403 || response.status === 404) {
            setErrorMessage(
                response.message ||
                    "The client could not be accessed. Your confirmation was not saved.",
            );
            return;
        }

        setErrorMessage(
            response.message ||
                "The inactive reason could not be saved. Check your connection and try again.",
        );
    };

    return (
        <ClientInactivityConfirmationContext.Provider
            value={{ requestInactiveConfirmation }}
        >
            {children}
            <ClientInactivityConfirmationModal
                open={Boolean(activeRequest)}
                transition={activeRequest?.transition}
                onConfirm={handleConfirm}
                errorMessage={errorMessage}
            />
        </ClientInactivityConfirmationContext.Provider>
    );
};

export const useClientInactivityConfirmation = () => {
    return useContext(ClientInactivityConfirmationContext);
};
